<template lang="pug">
  .organization-members
    WtTitle(child).is-size-3.has-text-weight-light.stat-title Members
    .wt-entity-table
      .tabs
        ul
          li(:class="{ 'is-active': activeTab === 'activeMembers' }" @click="setActiveTab('activeMembers')")
            a Active Members ({{ activeMembersCount }})
          li(:class="{ 'is-active': activeTab === 'allMembers' }" @click="setActiveTab('allMembers')")
            a All Program Members
      .div(v-if="activeTab === 'activeMembers'")
        .search-container
          BField.search-map-field(type="is-primary")
            BInput(
              :placeholder="'Search Active Members...'"
              type="search"
              icon="magnify"
              v-model="searchFilterActive"
              @input="onSearchActiveMembers"
            )
          .refresh-button-container
            WtButton(
              color="white"
              @click.native="refreshActiveMembers"
            )
              BIcon(
                icon="refresh",
                style="color:#2972F0")
              span.refresh-text Refresh
        BTable(
          :loading="isActiveTableLoading"
          :data="displayedMembersOrganization"
          :default-sort="['id', 'asc']"
          :backend-pagination="true"
          :paginated="true"
          :current-page="activeMembersPagination.currentPage"
          :total="_.get(organizationMembersResponseObject, 'pagination.total', 0)"
          :per-page="_.get(organizationMembersResponseObject, 'pagination.perPage', 10)"
          @page-change="onPageChangeActiveMembers"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
          class="full-width-table"
        )
          BTableColumn(field="username" label="Member" v-slot="column" sortable )
            .member-container
              img.profile-photo(:src="column.row.avatar")
              .member-details
                div {{ column.row.firstName }} {{ column.row.lastName }}
                a.username-link(:href="`/profile/${column.row.username}`") @{{ column.row.username }}
          BTableColumn(field="email" label="Email" sortable v-slot="column" :style="{ width: '30%' }")
            .email-container
              a(v-if="column.row.email" :href="`mailto:${column.row.email}`") {{ column.row.email }}
              span(v-else) N/A
          BTableColumn(label="Remove Member" v-slot="column" :style="{ width: '20%' }")
            .button-container
              WtButton.mt-1.is-outlined-button(
                bold
                size="small"
                @click.native="toggleRemoveMember(column.row.id)"
                :loading="isMemberLoading(column.row.id)"
              )
                span {{ isMemberRemoved(column.row.id) ? 'Undo' : 'Remove Member' }}
      .div(v-else)
        .search-container
          BField.search-map-field(type="is-primary")
            BInput(
              :placeholder="'Search All Members...'"
              type="search"
              icon="magnify"
              v-model="searchFilterAll"
              @input="onSearchAllMembers(searchFilterAll)"
            )
          .refresh-button-container
            WtButton(
              color="white"
              @click.native="refreshAllMembers"
            )
              BIcon(
                icon="refresh"
                style="color:#2972F0"
              )
              span.refresh-text Refresh
        BTable(
          :loading="isAllTableLoading"
          :data="displayedMembers"
          :default-sort="['id', 'asc']"
          :paginated="true"
          :backend-pagination="true"
          :current-page="membersResponseObject.currentPage"
          :total="_.get(membersResponseObject, 'pagination.total', 0)"
          :per-page="_.get(membersResponseObject, 'pagination.perPage', 10)"
          @page-change="onPageChangeAllMembers"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
          class="full-width-table"
        )
          BTableColumn(field="username" label="Member" v-slot="column" sortable)
            .member-container
              img.profile-photo(:src="column.row.avatar")
              .member-details
                div {{ column.row.firstName }} {{ column.row.lastName }}
                a.username-link(:href="`/profile/${column.row.username}`") @{{ column.row.username }}
          BTableColumn(field="email" label="Email" sortable v-slot="column")
            .email-container
              a(v-if="column.row.email" :href="`mailto:${column.row.email}`") {{ column.row.email }}
              span(v-else) N/A
          BTableColumn(label="Add Member" v-slot="column")
            .button-container
              WtButton.mt-1.is-centered(:class="isMemberRemoved(column.row.id) || isMemberAdded(column.row.id) ||  column.row.organizationId === organizationId ? 'is-outlined-button' : 'is-solid-button'"
                bold
                size="small"
                :disabled="(column.row.organizationId !== null && column.row.organizationId !== organizationId)"
                @click.native="handleMemberAction(column.row.id, column.row.organizationId)"
                :loading="isMemberLoading(column.row.id)"
              )
                span(v-if="isMemberAdded(column.row.id) || isMemberRemoved(column.row.id)") Undo
                span(v-else-if="column.row.organizationId === organizationId") Remove Member
                span(v-else) Add Member
</template>

<script>
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton.vue";
import { mapActions, mapGetters } from "vuex";

const ACTIVE_MEMBERS = "activeMembers";
const ALL_MEMBERS = "allMembers";

export default {
  name: "OrganizationMembersNew",

  components: {
    WtButton,
    WtTitle,
    WtTile
  },

  props: {
    organizationId: {
      type: Number,
      default: 0
    },
    perPage: {
      type: Number,
      default: null
    },
    membersCount: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      sortBy: null,
      searchFilterActive: null,
      searchFilterAll: null,
      activeTab: ACTIVE_MEMBERS,
      activeMembersPagination: { currentPage: 1, perPage: this.perPage || 10 },
      allMembersPagination: { currentPage: 1, perPage: this.perPage || 10 },
      removedMembers: {},
      addedMembers: {},
      changedMemberCount: 0,
      isActiveTableLoading: false,
      isAllTableLoading: false,
      loadingMembers: {}
    };
  },

  computed: {
    ...mapGetters("organizations", ["organizationMembersResponseObject"]),
    ...mapGetters("program", ["membersResponseObject"]),

    isMemberRemoved() {
      return memberId => !!this.removedMembers[memberId];
    },

    isMemberAdded() {
      return memberId => !!this.addedMembers[memberId];
    },

    isMemberLoading() {
      return memberId => !!this.loadingMembers[memberId];
    },

    displayedMembersOrganization() {
      return this._.get(this.organizationMembersResponseObject, "data", []);
    },

    displayedMembers() {
      return this._.get(this.membersResponseObject, "data", []);
    },

    activeMembersCount() {
      return (
        this._.get(
          this.organizationMembersResponseObject,
          "pagination.total",
          0
        ) + this.changedMemberCount
      );
    }
  },

  watch: {
    organizationId() {
      if (this.organizationId) {
        this.fetchMembers(ACTIVE_MEMBERS);
        this.fetchMembers(ALL_MEMBERS);
      }
    },
    sortBy() {
      this.fetchMembers(this.activeTab, 1);
    }
  },

  async mounted() {
    this.onSearchActiveMembers = this._.debounce(() => {
      this.fetchMembers(ACTIVE_MEMBERS, 1);
    }, 500);

    this.onSearchAllMembers = this._.debounce(query => {
      this.fetchMembers(ALL_MEMBERS, 1, query);
    }, 500);
  },

  methods: {
    ...mapActions("program", ["searchMembers", "setMembersResponseObject"]),
    ...mapActions("organizations", [
      "fetchOrganizationMembers",
      "addMemberToOrganization",
      "removeMemberFromOrganization"
    ]),

    async toggleRemoveMember(memberId) {
      const payload = { memberId, organizationId: this.organizationId };
      this.$set(this.loadingMembers, memberId, true);

      if (this.isMemberRemoved(memberId)) {
        // Undo Remove
        this.addMemberToOrganization(payload)
          .then(() => {
            this.$set(this.removedMembers, memberId, false);
            this.changedMemberCount += 1;
          })
          .finally(() => this.$set(this.loadingMembers, memberId, false));
      } else {
        // Remove the member
        this.removeMemberFromOrganization(payload)
          .then(() => {
            this.$set(this.removedMembers, memberId, true);
            this.changedMemberCount -= 1;
          })
          .finally(() => this.$set(this.loadingMembers, memberId, false));
      }
    },

    async handleMemberAction(memberId, currentOrganizationId) {
      const payload = { memberId, organizationId: this.organizationId };
      this.$set(this.loadingMembers, memberId, true);

      if (this.isMemberAdded(memberId)) {
        // Undo Add
        this.removeMemberFromOrganization(payload)
          .then(() => {
            this.$delete(this.addedMembers, memberId);
            this.changedMemberCount -= 1;
          })
          .finally(() => this.$set(this.loadingMembers, memberId, false));
      } else if (this.isMemberRemoved(memberId)) {
        // Undo Remove
        this.addMemberToOrganization(payload)
          .then(() => {
            this.$delete(this.removedMembers, memberId);
            this.changedMemberCount += 1;
          })
          .finally(() => this.$set(this.loadingMembers, memberId, false));
      } else if (currentOrganizationId === this.organizationId) {
        // Remove Member
        this.removeMemberFromOrganization(payload)
          .then(() => {
            this.$set(this.removedMembers, memberId, true);
            this.changedMemberCount -= 1;
          })
          .finally(() => this.$set(this.loadingMembers, memberId, false));
      } else {
        // Add Member
        this.addMemberToOrganization(payload)
          .then(() => {
            this.$set(this.addedMembers, memberId, true);
            this.changedMemberCount += 1;
          })
          .finally(() => this.$set(this.loadingMembers, memberId, false));
      }
    },

    fetchMembers(tab, page = 1, query = null) {
      if (tab === ACTIVE_MEMBERS) {
        this.isActiveTableLoading = true;
        this.activeMembersPagination.currentPage = page;
        this.fetchOrganizationMembers({
          organizationId: this.organizationId,
          perPage: this.activeMembersPagination.perPage,
          page: page,
          sortBy: this.sortBy,
          searchFilter: this.searchFilterActive
        })
          .then(() => {
            this.addedMembers = {};
            this.removedMembers = {};
            this.changedMemberCount = 0;
          })
          .finally(() => {
            this.isActiveTableLoading = false;
          });
      } else if (tab === ALL_MEMBERS) {
        this.isAllTableLoading = true;
        this.allMembersPagination.currentPage = page;
        this.searchMembers({
          programId: this.$auth.user().programId,
          perPage: this.allMembersPagination.perPage,
          page: page,
          query: query || this.searchFilterAll || "",
          sortBy: this.sortBy
        })
          .then(response => {
            this.setMembersResponseObject(response);
            this.addedMembers = {};
            this.removedMembers = {};
            this.changedMemberCount = 0;
          })
          .finally(() => {
            this.isAllTableLoading = false;
          });
      }
    },

    async refreshActiveMembers() {
      await this.fetchMembers(
        ACTIVE_MEMBERS,
        this.activeMembersPagination.currentPage
      );
    },

    async refreshAllMembers() {
      await this.fetchMembers(
        ALL_MEMBERS,
        this.allMembersPagination.currentPage
      );
    },

    onPageChangeActiveMembers(page) {
      this.fetchMembers(ACTIVE_MEMBERS, page);
    },

    onPageChangeAllMembers(page) {
      this.fetchMembers(ALL_MEMBERS, page);
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },

    onSearchActiveMembers() {
      // Placeholder to prevent warnings
    },

    onSearchAllMembers() {
      // Placeholder to prevent warnings
    }
  }
};
</script>

<style lang="sass" scoped>
.organization-members
  width: 100%
.stat-title
  font-family: Manrope
.wt-entity-table
  width: 100%
  background-color: white
  padding: 32px 24px 24px 24px
  border-radius: 12px
.search-map-field
  width: 376px
  padding-top: 12px
.search-container
  display: flex
  flex-direction: row
  justify-content: space-between
.refresh-text
  font-family: Manrope
  size: 14px
  color: #2972F0
.refresh-button-container
  display: flex
  align-items: center
  justify-content: center

.tabs
  li
    font-size: 14px
    cursor: pointer
    flex-grow: 1
    &.is-active
      font-weight: bold

.full-width-table
  width: 100%

.member-container
  display: flex
  align-items: center
  padding-left: 10px
  font-size: 14px

  @media (max-width: 1280px)
    padding-right: 0
.email-container
  padding-top: 5px
  font-size: 14px

.button-container
  display: flex
  justify-content: flex-end
  align-items: center

::v-deep
  th
    font-size: 14px
  th:nth-child(3)
    display: flex
    justify-content: flex-end
  td:nth-child(1)
    width: 50%
  td:nth-child(2)
    width: 30%
  td:nth-child(3)
    width: 20%

.profile-photo
  width: 40px
  height: 40px
  border-radius: 50%
  object-fit: cover
  margin-right: 10px

.member-details
  display: flex
  flex-direction: column

.username-link
  font-size: 0.9em
  color: #555
  text-decoration: none
  &:hover
    text-decoration: underline

.wt-button
  border-radius: 8px !important
  &.is-solid-button
    color: white
    background-color: #007bff !important
    border-color: #007bff
    &:hover
      color: white !important
      background-color: #0056b3
    &[disabled]
      background-color: #d3d3d3 !important
      border-color: #d3d3d3 !important
      cursor: not-allowed

  &.is-outlined-button
    background-color: white !important
    border-color: #007bff
    color: #007bff
    &:after
      border-color: #007bff #007bff #fff #fff !important

    &:hover
      color: #007bff !important
      background-color: #0056b3
      border-color: #0056b3
</style>
